import { useMemo, type ReactNode } from "react";
import type { NumberedRun } from "../lib/run-numbering";

interface RunsTableProps {
  /** Already-numbered runs, sorted descending by startedAt (newest first). */
  runs: NumberedRun[];
  /** IANA timezone name used to render the Timestamp column. */
  timeZone: string;
  /**
   * Optional Slice 6 hook — when supplied, each row beyond the oldest renders
   * a "Compare to #N-1" button that calls this callback with the current run
   * id and the next-older run id (the row directly below it in the table).
   * Slice 5 leaves this undefined; the button stays inert as a visual cue
   * for the upcoming capability.
   */
  onCompare?: (currentRunId: string, priorRunId: string) => void;
}

/**
 * Formats a UTC ISO timestamp in the schedule's local timezone as
 * `MMM d, h:mm a` (e.g. "Jun 25, 12:30 PM"). Returns the literal em-dash
 * placeholder string when the timestamp is invalid OR the timezone is
 * unrecognized — match the Phase 4 `Dash` pattern from sections-table.
 */
function formatRunTimestamp(iso: string, timeZone: string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return "—";
  try {
    return new Intl.DateTimeFormat("en-US", {
      month: "short",
      day: "numeric",
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
      timeZone,
    }).format(date);
  } catch {
    return "—";
  }
}

/**
 * Composes the Issues column copy: red + amber counts joined by " · ". When
 * both counts are zero, falls back to "0 issues" so the cell never reads as
 * blank. Returned as JSX rather than a plain string so each count can carry
 * its own color token.
 */
function IssuesCell({
  errors,
  warnings,
}: {
  errors: number;
  warnings: number;
}) {
  if (errors === 0 && warnings === 0) {
    return <span className="text-text-soft">0 issues</span>;
  }
  const parts: ReactNode[] = [];
  if (errors > 0) {
    parts.push(
      <span key="errors" className="text-red-400 font-medium">
        {errors} {errors === 1 ? "error" : "errors"}
      </span>,
    );
  }
  if (warnings > 0) {
    parts.push(
      <span key="warnings" className="text-amber-400 font-medium">
        {warnings} {warnings === 1 ? "warning" : "warnings"}
      </span>,
    );
  }
  return (
    <span>
      {parts.map((node, idx) => (
        <span key={idx}>
          {idx > 0 && <span className="text-text-faint"> · </span>}
          {node}
        </span>
      ))}
    </span>
  );
}

/**
 * Pure presentation of the runs history table. Matches the prototype's
 * 6-column layout (Run, Timestamp, Run by, Issues, Placed, Compare). The
 * parent {@link RunsTab} handles loading / error / empty states above this
 * component; an empty {@link runs} array intentionally renders an empty
 * `<tbody>` rather than its own empty state so the wrapper's copy stays the
 * single source of truth.
 */
export function RunsTable({ runs, timeZone, onCompare }: RunsTableProps) {
  // Pre-compute the prior-run id for each row so the Compare handler doesn't
  // have to look up the next row at click time. Since the input is sorted
  // descending (newest at index 0), the "prior" run for row `i` is at row
  // `i + 1` — which exists iff `runNumber > 1`.
  const priorIds = useMemo(() => {
    return runs.map((_, idx) => runs[idx + 1]?.id ?? null);
  }, [runs]);

  return (
    <div
      data-testid="runs-table"
      className="bg-surface border border-border-subtle rounded-lg overflow-hidden"
    >
      <table className="w-full border-collapse text-[13px]">
        <thead>
          <tr className="border-b border-border-subtle">
            <th className="text-left px-4 py-3 font-semibold text-text-muted text-[12px] uppercase tracking-wider">
              Run
            </th>
            <th className="text-left px-4 py-3 font-semibold text-text-muted text-[12px] uppercase tracking-wider">
              Timestamp
            </th>
            <th className="text-left px-4 py-3 font-semibold text-text-muted text-[12px] uppercase tracking-wider">
              Run by
            </th>
            <th className="text-right px-4 py-3 font-semibold text-text-muted text-[12px] uppercase tracking-wider">
              Issues
            </th>
            <th className="text-right px-4 py-3 font-semibold text-text-muted text-[12px] uppercase tracking-wider">
              Placed
            </th>
            <th className="px-4 py-3" aria-label="Compare" />
          </tr>
        </thead>
        <tbody>
          {runs.map((run, idx) => {
            const isCurrent = idx === 0;
            const priorRunId = priorIds[idx];
            const canCompare = run.runNumber > 1 && priorRunId !== null;
            return (
              <tr
                key={run.id}
                data-testid={`runs-row-${run.id}`}
                className="border-b border-border-subtle last:border-b-0 hover:bg-surface-2/40"
              >
                <td className="px-4 py-3 h-[50px]">
                  <div className="flex items-center gap-2 font-semibold text-white">
                    <span data-testid={`runs-row-label-${run.id}`}>
                      Run #{run.runNumber}
                    </span>
                    {isCurrent && (
                      <span
                        data-testid={`runs-row-current-${run.id}`}
                        className="bg-blue-500/15 text-blue-300 border border-blue-500/30 px-2 py-0.5 rounded text-[11px] font-medium"
                      >
                        Current
                      </span>
                    )}
                  </div>
                </td>
                <td className="px-4 py-3 text-text-soft">
                  {formatRunTimestamp(run.startedAt, timeZone)}
                </td>
                <td
                  className="px-4 py-3 text-text-muted"
                  data-testid={`runs-row-by-${run.id}`}
                >
                  {run.runByUserDisplayName ?? "Unknown user"}
                </td>
                <td
                  className="px-4 py-3 text-right"
                  data-testid={`runs-row-issues-${run.id}`}
                >
                  <IssuesCell
                    errors={run.errorIssueCount}
                    warnings={run.warningIssueCount}
                  />
                </td>
                <td
                  className="px-4 py-3 text-right text-text-soft"
                  data-testid={`runs-row-placed-${run.id}`}
                >
                  {run.assignmentCount.toLocaleString()}
                </td>
                <td className="px-4 py-3 text-right">
                  {canCompare ? (
                    <button
                      type="button"
                      data-testid={`runs-row-compare-${run.id}`}
                      onClick={
                        onCompare && priorRunId
                          ? () => onCompare(run.id, priorRunId)
                          : undefined
                      }
                      className="text-blue-400 hover:text-blue-300 text-[13px] font-medium"
                    >
                      Compare to #{run.runNumber - 1}
                    </button>
                  ) : (
                    <span className="text-text-faint">—</span>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
